import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { businessProfileService } from '../../services/businessProfileService';
import { tpayService } from '../../services/tpayService';
import { facilityService } from '../../services/facilityService';
import BusinessProfileForm from './BusinessProfileForm';
import FormSection from './FormSection';
import { FacilitiesSection } from '../Facilities/FacilitiesSection';
import { ProductsSection } from '../Products/ProductsSection';
import { AgentsSection } from '../Agents/AgentsSection';
import { TrainersSection } from '../Trainers/TrainersSection';
import { KSeFSection } from '../KSeF/KSeFSection';
import { ReportsSection } from '../Reports/ReportsSection';
import { getEmptyFormData } from '../../utils/businessProfileValidation';
import { copyBookingLink } from '../../utils/deepLinks';
import type { BusinessProfileFormData, LegalForm, TPayCategory, BusinessProfileDetail } from '../../types/businessProfile';
import type { Facility } from '../../types/facility';
import { ApiError } from '../../services/apiClient';

export default function BusinessProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<BusinessProfileFormData | null>(null);
  const [fullProfile, setFullProfile] = useState<BusinessProfileDetail | null>(null);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [legalForms, setLegalForms] = useState<LegalForm[]>([]);
  const [categories, setCategories] = useState<TPayCategory[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Section collapse state
  const [collapsedSections, setCollapsedSections] = useState({
    basicInfo: true,
    facilities: true,
    products: true,
    agents: true,
    trainers: true,
    ksef: true,
    reports: true,
  });

  const toggleSection = (section: keyof typeof collapsedSections) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Load facilities for product assignment
  const loadFacilities = useCallback(async () => {
    if (!token) return;
    try {
      const data = await facilityService.getMyFacilities(token);
      setFacilities(data);
    } catch {
      // Silently fail - facilities are optional
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [token]);

  useEffect(() => {
    if (profile?.id) {
      loadFacilities();
    }
  }, [profile?.id, loadFacilities]);

  const loadData = async () => {
    if (!token) return;

    setIsLoading(true);
    setLoadError(null);

    try {
      // Load TPay dictionaries in parallel
      const [legalFormsData, categoriesData] = await Promise.all([
        tpayService.getLegalForms(token),
        tpayService.getCategories(token),
      ]);
      setLegalForms(legalFormsData);
      setCategories(categoriesData);

      // Try to load existing profile
      try {
        const existingProfile = await businessProfileService.getProfile(token);
        setProfile(mapProfileToFormData(existingProfile));
        setFullProfile(existingProfile);
        setIsNewProfile(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          // No profile exists, show empty form
          setProfile(getEmptyFormData());
          setFullProfile(null);
          setIsNewProfile(true);
        } else {
          throw err;
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      setLoadError(message);
      showToast('error', t('common.error'), message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (formData: BusinessProfileFormData, facilityPlan?: File) => {
    if (!token || !profile) return;

    setIsSaving(true);

    try {
      if (isNewProfile) {
        await businessProfileService.createProfile(token, formData, facilityPlan);
        showToast('success', t('businessProfile.messages.createSuccess'));
      } else {
        await businessProfileService.updateProfile(token, profile.id!, formData, facilityPlan);
        showToast('success', t('businessProfile.messages.updateSuccess'));
      }

      // Refresh profile data
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save profile';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyBookingLink = async () => {
    if (!profile?.id) return;
    const ok = await copyBookingLink(profile.id);
    if (ok) {
      showToast('success', t('common.linkCopied'));
    } else {
      showToast('error', t('common.linkCopyError'));
    }
  };

  const handleAvatarUpload = async (file: File) => {
    if (!token || !profile?.id) return;

    try {
      const result = await businessProfileService.uploadAvatar(token, profile.id, file);
      setProfile(prev => prev ? { ...prev, avatarUrl: result.avatarUrl } : null);
      showToast('success', t('businessProfile.messages.avatarSuccess'));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload avatar';
      showToast('error', t('common.error'), message);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Skeleton Header */}
        <div className="mb-6">
          <div className="h-8 bg-surface-card rounded w-64 animate-pulse mb-2" />
          <div className="h-5 bg-surface-app-elevated rounded w-96 animate-pulse" />
        </div>

        {/* Skeleton Form Sections */}
        <div className="space-y-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-surface-app-elevated rounded-lg border border-border-strong p-4">
              <div className="h-6 bg-surface-card rounded w-48 animate-pulse mb-4" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-10 bg-surface-card rounded animate-pulse" />
                <div className="h-10 bg-surface-card rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-brand-warm/30 border border-brand-warm text-white px-6 py-8 rounded-lg text-center">
          <svg className="w-12 h-12 mx-auto mb-4 opacity-75" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-xl font-semibold mb-2">{t('common.error')}</h2>
          <p className="mb-4">{loadError}</p>
          <button
            onClick={loadData}
            className="px-6 py-2 bg-brand-warm hover:bg-brand-warm-deep text-white rounded-lg transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-ink-primary">
            {isNewProfile
              ? t('businessProfile.createTitle')
              : t('businessProfile.editTitle')}
          </h1>
          <p className="text-ink-secondary mt-1">
            {isNewProfile
              ? t('businessProfile.createDescription')
              : t('businessProfile.editDescription')}
          </p>
        </div>
        {!isNewProfile && profile?.id && (
          <button
            type="button"
            onClick={handleCopyBookingLink}
            title={t('businessProfile.copyLinkTooltip')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm font-medium flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {t('businessProfile.copyLink')}
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Basic Info Section */}
        <FormSection
          title={t('businessProfile.sections.basicInfo')}
          isOpen={!collapsedSections.basicInfo}
          onToggle={() => toggleSection('basicInfo')}
          variant="dark"
        >
          {profile && (
            <BusinessProfileForm
              initialData={profile}
              legalForms={legalForms}
              categories={categories}
              onSave={handleSave}
              onAvatarUpload={handleAvatarUpload}
              isSaving={isSaving}
              isNewProfile={isNewProfile}
            />
          )}
        </FormSection>

        {/* Facilities Section - only show for existing profiles */}
        {!isNewProfile && profile?.id && (
          <FacilitiesSection
            businessProfileId={profile.id}
            isCollapsed={collapsedSections.facilities}
            onToggle={() => toggleSection('facilities')}
          />
        )}

        {/* Products Section - only show for existing profiles */}
        {!isNewProfile && profile?.id && (
          <ProductsSection
            businessProfileId={profile.id}
            facilities={facilities}
            isCollapsed={collapsedSections.products}
            onToggle={() => toggleSection('products')}
          />
        )}

        {/* Agents Section - only show for existing profiles */}
        {!isNewProfile && profile?.id && (
          <AgentsSection
            businessProfileId={profile.id}
            isCollapsed={collapsedSections.agents}
            onToggle={() => toggleSection('agents')}
          />
        )}

        {/* Trainers Section - only show for existing profiles */}
        {!isNewProfile && profile?.id && (
          <TrainersSection
            businessProfileId={profile.id}
            isCollapsed={collapsedSections.trainers}
            onToggle={() => toggleSection('trainers')}
          />
        )}

        {/* KSeF Section - only show for existing profiles */}
        {!isNewProfile && profile?.id && (
          <KSeFSection
            businessProfile={fullProfile}
            isCollapsed={collapsedSections.ksef}
            onToggle={() => toggleSection('ksef')}
            onProfileUpdate={loadData}
          />
        )}

        {/* Reports Section - only show for existing profiles */}
        {!isNewProfile && profile?.id && (
          <ReportsSection
            businessProfileId={profile.id}
            isCollapsed={collapsedSections.reports}
            onToggle={() => toggleSection('reports')}
          />
        )}

        {/* Embed Widget — links to its own page so the editing surface
            isn't squeezed inside this already-busy profile view. */}
        {!isNewProfile && profile?.id && (
          <div className="bg-surface-card border border-border-subtle rounded-xl p-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-base font-semibold text-ink-primary m-0">
                {t('embedWidget.pageTitlePrefix')}{' '}
                <span className="font-mono uppercase tracking-[0.12em] text-brand-primary text-sm align-middle">
                  Plugin Commerce
                </span>
              </h2>
              <p className="text-ink-muted text-sm mt-1 mb-0">
                {t('embedWidget.navCard.description')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/settings/widget')}
              className="px-4 py-2 bg-brand-primary text-white border-0 rounded-lg font-semibold cursor-pointer whitespace-nowrap"
            >
              {t('embedWidget.navCard.openButton')}
            </button>
          </div>
        )}
      </div>

      {/* Version stamp */}
      <div className="mt-6 text-right text-[10px] text-ink-muted tracking-wider font-mono select-all">
        v{__APP_VERSION__}
      </div>
    </div>
  );
}

// Helper function to map API response to form data
function mapProfileToFormData(profile: BusinessProfileDetail): BusinessProfileFormData {
  return {
    id: profile.id,
    nip: profile.nip || '',
    companyName: profile.companyName || '',
    displayName: profile.displayName || '',
    address: profile.address || '',
    city: profile.city || '',
    postalCode: profile.postalCode || '',
    email: profile.email || '',
    phoneNumber: profile.phoneNumber || '',
    phoneCountry: profile.phoneCountry || 'PL',
    regon: profile.regon || '',
    krs: profile.krs || '',
    legalForm: profile.legalForm || 0,
    categoryId: profile.categoryId || 0,
    website: profile.website || '',
    websiteDescription: profile.websiteDescription || '',
    contactPersonName: profile.contactPersonName || '',
    contactPersonSurname: profile.contactPersonSurname || '',
    latitude: profile.latitude,
    longitude: profile.longitude,
    avatarUrl: profile.avatarUrl,
    facilityPlanUrl: profile.facilityPlanUrl,
    facilityPlanFileName: profile.facilityPlanFileName,
    termsAndConditionsUrl: profile.termsAndConditionsUrl || '',
    // Schedule data
    weekdaysSchedule: profile.weekdaysSchedule || [],
    saturdaySchedule: profile.saturdaySchedule || [],
    sundaySchedule: profile.sundaySchedule || [],
    dateSpecificAvailability: profile.dateSpecificAvailability || {},
  };
}
